import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useTheme } from '@mui/material/styles';
import MuxPlayer from '@mux/mux-player-react';
import videojs from '@mux/videojs-kit';
import '@mux/videojs-kit/dist/index.css';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Box } from 'src/components/Box';
import { useSession } from 'src/contexts/SessionContext';
import { fetchVideo } from 'src/models/video/video';
import { Identifier } from 'src/types/utilityTypes';

import { CircularProgress } from './CircularProgress';
import { Icon } from './Icon';
import { IconButton } from './IconButton';

interface VideoProps {
  playbackId?: string;
  videoId?: Identifier;
  videoUrl?: string;
  onLoadedData?: () => void;
  downloadable?: boolean;
  refetchInterval?: false | number;
  embedUrl?: string;
}

export const Video = ({
  playbackId,
  videoId,
  videoUrl,
  embedUrl,
  onLoadedData,
  downloadable,
  refetchInterval,
}: VideoProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'video' });
  const theme = useTheme();
  const { user, session } = useSession();
  const [videoPlaybackId, setVideoPlaybackId] = useState(playbackId);
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<any>(null);

  const { data: fetchedVideo, isLoading } = useQuery({
    queryKey: ['video', videoId],

    queryFn: async () => {
      if (!videoId) {
        throw new Error(t('videoIdRequired'));
      }
      const video = await fetchVideo({ id: videoId });
      if (video.playbackId) {
        setVideoPlaybackId(video.playbackId);
      }
      return video;
    },

    enabled: !!videoId && !videoPlaybackId,

    // Poll every second if playbackId is not set
    refetchInterval:
      refetchInterval !== undefined
        ? refetchInterval
        : videoPlaybackId
        ? false
        : 1000,
  });

  // Initialize video.js for m3u8 streams
  useEffect(() => {
    // Always clean up previous instance first
    if (playerRef.current) {
      try {
        playerRef.current.dispose();
      } catch (e) {
        // noop
      }
      playerRef.current = null;
    }

    // Small delay to ensure DOM is ready
    const initializePlayer = () => {
      if (!videoPlaybackId && videoUrl?.includes('.m3u8') && videoRef.current) {
        try {
          playerRef.current = videojs(videoRef.current, {
            fluid: true,
            controls: true,
            sources: [
              {
                src: videoUrl,
                type: 'application/x-mpegURL',
              },
            ],
          });

          if (onLoadedData) {
            playerRef.current.on('loadeddata', onLoadedData);
          }
        } catch (e) {
          // noop
        }
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const rafId = requestAnimationFrame(initializePlayer);

    return () => {
      cancelAnimationFrame(rafId);
      if (playerRef.current) {
        try {
          playerRef.current.dispose();
        } catch (e) {
          // noop
        }
        playerRef.current = null;
      }
    };
  }, [videoUrl, onLoadedData, videoPlaybackId, isLoading]);

  const downloadURL =
    downloadable && videoPlaybackId
      ? `https://stream.mux.com/${videoPlaybackId}/high.mp4?download=video.mp4`
      : null;

  if (!playbackId && !videoId && !videoUrl) {
    return null;
  }

  if (isLoading) {
    return <CircularProgress />;
  }

  if (videoPlaybackId) {
    return (
      <Box sx={{ position: 'relative' }}>
        <MuxPlayer
          playbackId={videoPlaybackId}
          accentColor={theme.palette.primary.ogSmart}
          style={
            {
              aspectRatio: 16 / 9,
              minWidth: 200,
              '--seek-forward-button': 'none',
              '--seek-backward-button': 'none',
            } as React.CSSProperties
          }
          onLoadedData={onLoadedData}
          metadata={{
            video_id: videoId,
            viewer_user_id: user.id,
            sub_property_id: session.id,
          }}
        />
        {downloadURL && (
          <IconButton
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              color: 'white',
            }}
            href={downloadURL}
            size="small"
          >
            <Icon
              icon={icon({ name: 'download', style: 'solid' })}
              size="small"
            />
          </IconButton>
        )}
      </Box>
    );
  }

  if (videoUrl?.includes('.m3u8')) {
    return (
      <Box sx={{ position: 'relative' }}>
        <div data-vjs-player>
          <video
            ref={videoRef}
            className="video-js"
            style={{
              aspectRatio: '16/9',
              width: '100%',
              minWidth: 200,
            }}
          />
        </div>
      </Box>
    );
  }

  const url = fetchedVideo?.embedUrl || embedUrl || videoUrl;

  if (url && url.includes('https://vimeo.com')) {
    return <Box>{t('please save the video')}</Box>;
  } else if (url) {
    return <NonMuxPlayer embedUrl={url} onLoadedData={onLoadedData} />;
  }

  return <Box>{t('processing')}</Box>;
};

const NonMuxPlayer: React.FC<{
  embedUrl: string;
  onLoadedData?: () => void;
}> = ({ embedUrl, onLoadedData }) => {
  const convertedEmbedUrl = convertToEmbedUrl(embedUrl);
  return (
    <iframe
      src={convertedEmbedUrl}
      width="100%"
      height="100%"
      frameBorder="0"
      allow="autoplay; fullscreen; picture-in-picture"
      allowFullScreen
      onLoad={onLoadedData}
      style={{ aspectRatio: '16 / 9', minWidth: 200 }}
    />
  );
};

const convertToEmbedUrl = (url: string) => {
  if (url.includes('vimeo.com')) {
    return `https://player.vimeo.com/video/${url.split('/').pop()}`;
  } else if (
    (url.includes('youtube.com') || url.includes('youtu.be')) &&
    url.split('v=')[1]
  ) {
    return `https://www.youtube.com/embed/${url.split('v=')[1]}`;
  }
  return url;
};
